import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchPaymentsForPeriod, formatDate, periodOptions } from "../../services/adminService";

export default function PaymentHistory() {
  const [period, setPeriod] = useState(String(new Date().getFullYear()));
  const [records, setRecords] = useState([]);
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const map = await fetchPaymentsForPeriod(period);
      setRecords(
        Object.values(map).sort((a, b) => (a.memberName || "").localeCompare(b.memberName || ""))
      );
    } catch (err) {
      console.error(err);
      setError("Could not load payments.");
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    load();
  }, [load]);

  const shown = useMemo(
    () => records.filter((r) => status === "all" || r.status === status),
    [records, status]
  );
  const collected = useMemo(
    () => records.filter((r) => r.status === "paid").reduce((sum, r) => sum + (Number(r.amount) || 0), 0),
    [records]
  );

  return (
    <>
      <header className="na-page-head">
        <div>
          <h1>Payment history</h1>
          <p className="na-muted">Every payment recorded for the selected membership period.</p>
        </div>
        <div className="na-head-actions">
          <button className="na-btn" onClick={load}>Refresh</button>
        </div>
      </header>

      <div className="na-period-row">
        <label className="na-inline">
          <strong>Membership period</strong>
          <select value={period} onChange={(e) => setPeriod(e.target.value)}>
            {periodOptions().map((y) => (
              <option key={y}>{y}</option>
            ))}
          </select>
        </label>
        <label className="na-inline">
          <strong>Status</strong>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="all">All</option>
            <option value="paid">Paid</option>
            <option value="unpaid">Unpaid</option>
          </select>
        </label>
      </div>

      <section className="na-stats na-stats-2">
        <div className="na-card">
          <span className="na-muted">Total collected</span>
          <strong>KES {collected.toLocaleString()}</strong>
          <small>{period} membership period</small>
        </div>
        <div className="na-card">
          <span className="na-muted">Payment records</span>
          <strong>{records.length}</strong>
          <small>Members without a record show as unknown in the register</small>
        </div>
      </section>

      <section className="na-card na-table-card">
        {error && <p className="na-error na-pad">{error}</p>}
        <div className="na-table-wrap">
          <table className="na-table">
            <thead>
              <tr>
                <th>Member</th><th>Status</th><th>Amount</th><th>Method</th><th>Reference</th><th>Date paid</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="6" className="na-empty">Loading payments…</td></tr>}
              {!loading && shown.length === 0 && <tr><td colSpan="6" className="na-empty">No payment records.</td></tr>}
              {!loading &&
                shown.map((r) => (
                  <tr key={r.id}>
                    <td><strong>{r.memberName}</strong></td>
                    <td><span className={`na-badge na-badge-${r.status}`}>{r.status === "paid" ? "Paid" : "Unpaid"}</span></td>
                    <td>{r.status === "paid" ? `KES ${Number(r.amount || 0).toLocaleString()}` : "—"}</td>
                    <td>{r.method || "—"}</td>
                    <td>{r.reference || "—"}</td>
                    <td>{r.paidOn ? formatDate(r.paidOn) : "—"}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
